import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Box, Button, Snackbar, Typography } from '@mui/material';
import EmptyState from '../../Common/EmptyState';
import AddIcon from '@mui/icons-material/Add';
import adminApi from '../../../utils/admin.api';
import ConfirmDialog from '../../Common/ConfirmDialog';
import DemoTable from './DemoTable';
import DemoFormDialog from './DemoFormDialog';
import DemoPreviewDialog from './DemoPreviewDialog';

const APP_NAME = 'Intelliconvo';

const DemoManagement = () => {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Create / Update
  const [formOpen, setFormOpen] = useState(false);
  const [editDemo, setEditDemo] = useState(null); // null → create, object → edit
  const [saving, setSaving] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Preview
  const [preview, setPreview] = useState(null); // { url, title }

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') =>
    setSnack({ open: true, message, severity });

  // ── Read ─────────────────────────────────────────────────────────────────────

  const loadDemos = useCallback(() => {
    setLoading(true);
    adminApi
      .getCatalog(page + 1, rowsPerPage)
      .then(({ data }) => {
        setDemos(Array.isArray(data?.data?.items) ? data.data.items : []);
        setTotal(data?.data?.total ?? 0);
      })
      .catch(() => setError('Failed to load demos.'))
      .finally(() => setLoading(false));
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadDemos();
  }, [loadDemos]);

  // ── Create / Update ───────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditDemo(null);
    setFormOpen(true);
  };

  const openEdit = (demo) => {
    setEditDemo(demo);
    setFormOpen(true);
  };

  const handleSave = async (form) => {
    setSaving(true);
    setError('');
    try {
      if (editDemo) {
        const { title, url, description } = form;
        await adminApi.updateDemo(APP_NAME, editDemo.id, { title, url, description });
      } else {
        await adminApi.createDemo({ ...form, app: APP_NAME });
      }
      setFormOpen(false);
      loadDemos();
      showSnack(editDemo ? 'Demo updated successfully.' : 'Demo created successfully.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminApi.deleteDemo(APP_NAME, deleteTarget.id);
      setDeleteTarget(null);
      loadDemos();
      showSnack('Demo deleted successfully.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Form seed values ──────────────────────────────────────────────────────────

  const formInitial = editDemo
    ? {
        id: String(editDemo.id ?? ''),
        title: editDemo.title,
        url: editDemo.url,
        description: editDemo.description || '',
      }
    : null;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Demo Management</Typography>
          <Typography variant="body2" color="text.secondary">Application: {APP_NAME}</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add Demo
        </Button>
      </Box>

      {error && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography color="error">{error}</Typography>
          <Button size="small" onClick={loadDemos}>Retry</Button>
        </Box>
      )}

      {!loading && total === 0 ? (
        <EmptyState
          title="No demos yet"
          message='Click "Add Demo" to add the first demo for Intelliconvo.'
          actionLabel="Add Demo"
          onAction={openCreate}
        />
      ) : (
        <DemoTable
          demos={demos}
          loading={loading}
          onEdit={openEdit}
          onDelete={(demo) => setDeleteTarget(demo)}
          onPreview={(demo) => setPreview({ url: demo.url, title: demo.title })}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      )}

      {/* Create / Update */}
      <DemoFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
        initial={formInitial}
        saving={saving}
      />

      {/* Delete */}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete Demo"
        message={`Permanently delete demo "${deleteTarget?.id}"? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleting}
      />

      {/* Preview */}
      <DemoPreviewDialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        url={preview?.url}
        title={preview?.title}
      />

      {/* Success / Error toast */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DemoManagement;
